import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useMemo, useRef, useEffect, useState, useContext } from 'react';
import { useDndContext, getClientRect, useDroppable, useDraggable, closestCorners, getFirstCollision, getScrollableAncestors, KeyboardCode } from '@dnd-kit/core';
import { useUniqueId, useIsomorphicLayoutEffect, CSS, useCombinedRefs, isKeyboardEvent, subtract } from '@dnd-kit/utilities';
/**
 * Move an array item to a different position. Returns a new array with the item moved to the new position.
 */

function arrayMove(array, from, to) {
  var newArray = array.slice();
  newArray.splice(to < 0 ? newArray.length + to : to, 0, newArray.splice(from, 1)[0]);
  return newArray;
}
/**
 * Swap an array item to a different position. Returns a new array with the item swapped to the new position.
 */


function arraySwap(array, from, to) {
  var newArray = array.slice();
  newArray[from] = array[to];
  newArray[to] = array[from];
  return newArray;
}

function getSortedRects(items, rects) {
  return items.reduce(function (accumulator, id, index) {
    var rect = rects.get(id);

    if (rect) {
      accumulator[index] = rect;
    }

    return accumulator;
  }, Array(items.length));
}

function isValidIndex(index) {
  return index !== null && index >= 0;
}

function itemsEqual(a, b) {
  if (a === b) {
    return true;
  }

  if (a.length !== b.length) {
    return false;
  }

  for (var i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
}

function normalizeDisabled(disabled) {
  if (typeof disabled === 'boolean') {
    return {
      draggable: disabled,
      droppable: disabled
    };
  }

  return disabled;
} // To-do: We should be calculating scale transformation


var defaultScale = {
  scaleX: 1,
  scaleY: 1
};

var horizontalListSortingStrategy = function horizontalListSortingStrategy(_ref) {
  var _rects$activeIndex;

  var rects = _ref.rects,
      fallbackActiveRect = _ref.activeNodeRect,
      activeIndex = _ref.activeIndex,
      overIndex = _ref.overIndex,
      index = _ref.index;
  var activeNodeRect = (_rects$activeIndex = rects[activeIndex]) != null ? _rects$activeIndex : fallbackActiveRect;

  if (!activeNodeRect) {
    return null;
  }

  var itemGap = getItemGap(rects, index, activeIndex);

  if (index === activeIndex) {
    var newIndexRect = rects[overIndex];

    if (!newIndexRect) {
      return null;
    }

    return _objectSpread({
      x: activeIndex < overIndex ? newIndexRect.left + newIndexRect.width - (activeNodeRect.left + activeNodeRect.width) : newIndexRect.left - activeNodeRect.left,
      y: 0
    }, defaultScale);
  }

  if (index > activeIndex && index <= overIndex) {
    return _objectSpread({
      x: -activeNodeRect.width - itemGap,
      y: 0
    }, defaultScale);
  }

  if (index < activeIndex && index >= overIndex) {
    return _objectSpread({
      x: activeNodeRect.width + itemGap,
      y: 0
    }, defaultScale);
  }

  return _objectSpread({
    x: 0,
    y: 0
  }, defaultScale);
};

function getItemGap(rects, index, activeIndex) {
  var currentRect = rects[index];
  var previousRect = rects[index - 1];
  var nextRect = rects[index + 1];

  if (!currentRect || !previousRect && !nextRect) {
    return 0;
  }

  if (activeIndex < index) {
    return previousRect ? currentRect.left - (previousRect.left + previousRect.width) : nextRect.left - (currentRect.left + currentRect.width);
  }

  return nextRect ? nextRect.left - (currentRect.left + currentRect.width) : currentRect.left - (previousRect.left + previousRect.width);
}

var rectSortingStrategy = function rectSortingStrategy(_ref) {
  var rects = _ref.rects,
      activeIndex = _ref.activeIndex,
      overIndex = _ref.overIndex,
      index = _ref.index;
  var newRects = arrayMove(rects, overIndex, activeIndex);
  var oldRect = rects[index];
  var newRect = newRects[index];

  if (!newRect || !oldRect) {
    return null;
  }

  return {
    x: newRect.left - oldRect.left,
    y: newRect.top - oldRect.top,
    scaleX: newRect.width / oldRect.width,
    scaleY: newRect.height / oldRect.height
  };
};

var rectSwappingStrategy = function rectSwappingStrategy(_ref) {
  var activeIndex = _ref.activeIndex,
      index = _ref.index,
      rects = _ref.rects,
      overIndex = _ref.overIndex;
  var oldRect;
  var newRect;

  if (index === activeIndex) {
    oldRect = rects[index];
    newRect = rects[overIndex];
  }

  if (index === overIndex) {
    oldRect = rects[index];
    newRect = rects[activeIndex];
  }

  if (!newRect || !oldRect) {
    return null;
  }

  return {
    x: newRect.left - oldRect.left,
    y: newRect.top - oldRect.top,
    scaleX: newRect.width / oldRect.width,
    scaleY: newRect.height / oldRect.height
  };
}; // To-do: We should be calculating scale transformation


var defaultScale$1 = {
  scaleX: 1,
  scaleY: 1
};

var verticalListSortingStrategy = function verticalListSortingStrategy(_ref) {
  var _rects$activeIndex;

  var activeIndex = _ref.activeIndex,
      fallbackActiveRect = _ref.activeNodeRect,
      index = _ref.index,
      rects = _ref.rects,
      overIndex = _ref.overIndex;
  var activeNodeRect = (_rects$activeIndex = rects[activeIndex]) != null ? _rects$activeIndex : fallbackActiveRect;

  if (!activeNodeRect) {
    return null;
  }

  if (index === activeIndex) {
    var overIndexRect = rects[overIndex];

    if (!overIndexRect) {
      return null;
    }

    return _objectSpread({
      x: 0,
      y: activeIndex < overIndex ? overIndexRect.top + overIndexRect.height - (activeNodeRect.top + activeNodeRect.height) : overIndexRect.top - activeNodeRect.top
    }, defaultScale$1);
  }

  var itemGap = getItemGap$1(rects, index, activeIndex);

  if (index > activeIndex && index <= overIndex) {
    return _objectSpread({
      x: 0,
      y: -activeNodeRect.height - itemGap
    }, defaultScale$1);
  }

  if (index < activeIndex && index >= overIndex) {
    return _objectSpread({
      x: 0,
      y: activeNodeRect.height + itemGap
    }, defaultScale$1);
  }

  return _objectSpread({
    x: 0,
    y: 0
  }, defaultScale$1);
};

function getItemGap$1(clientRects, index, activeIndex) {
  var currentRect = clientRects[index];
  var previousRect = clientRects[index - 1];
  var nextRect = clientRects[index + 1];

  if (!currentRect) {
    return 0;
  }

  if (activeIndex < index) {
    return previousRect ? currentRect.top - (previousRect.top + previousRect.height) : nextRect ? nextRect.top - (currentRect.top + currentRect.height) : 0;
  }

  return nextRect ? nextRect.top - (currentRect.top + currentRect.height) : previousRect ? currentRect.top - (previousRect.top + previousRect.height) : 0;
}

var ID_PREFIX = 'Sortable';
var Context = /*#__PURE__*/React.createContext({
  activeIndex: -1,
  containerId: ID_PREFIX,
  disableTransforms: false,
  items: [],
  overIndex: -1,
  useDragOverlay: false,
  sortedRects: [],
  strategy: rectSortingStrategy,
  disabled: {
    draggable: false,
    droppable: false
  }
});

function SortableContext(_ref) {
  var children = _ref.children,
      id = _ref.id,
      userDefinedItems = _ref.items,
      _ref$strategy = _ref.strategy,
      strategy = _ref$strategy === void 0 ? rectSortingStrategy : _ref$strategy,
      _ref$disabled = _ref.disabled,
      disabledProp = _ref$disabled === void 0 ? false : _ref$disabled;

  var _useDndContext = useDndContext(),
      active = _useDndContext.active,
      dragOverlay = _useDndContext.dragOverlay,
      droppableRects = _useDndContext.droppableRects,
      over = _useDndContext.over,
      measureDroppableContainers = _useDndContext.measureDroppableContainers,
      measuringScheduled = _useDndContext.measuringScheduled;

  var containerId = useUniqueId(ID_PREFIX, id);
  var useDragOverlay = Boolean(dragOverlay.rect !== null);
  var items = useMemo(function () {
    return userDefinedItems.map(function (item) {
      return typeof item === 'object' && 'id' in item ? item.id : item;
    });
  }, [userDefinedItems]);
  var isDragging = active != null;
  var activeIndex = active ? items.indexOf(active.id) : -1;
  var overIndex = over ? items.indexOf(over.id) : -1;
  var previousItemsRef = useRef(items);
  var itemsHaveChanged = !itemsEqual(items, previousItemsRef.current);
  var disableTransforms = overIndex !== -1 && activeIndex === -1 || itemsHaveChanged;
  var disabled = normalizeDisabled(disabledProp);
  useIsomorphicLayoutEffect(function () {
    if (itemsHaveChanged && isDragging && !measuringScheduled) {
      measureDroppableContainers(items);
    }
  }, [itemsHaveChanged, items, isDragging, measureDroppableContainers, measuringScheduled]);
  useEffect(function () {
    previousItemsRef.current = items;
  }, [items]);
  var contextValue = useMemo(function () {
    return {
      activeIndex: activeIndex,
      containerId: containerId,
      disabled: disabled,
      disableTransforms: disableTransforms,
      items: items,
      overIndex: overIndex,
      useDragOverlay: useDragOverlay,
      sortedRects: getSortedRects(items, droppableRects),
      strategy: strategy
    };
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [activeIndex, containerId, disabled.draggable, disabled.droppable, disableTransforms, items, overIndex, droppableRects, useDragOverlay, strategy]);
  return React.createElement(Context.Provider, {
    value: contextValue
  }, children);
}

var defaultNewIndexGetter = function defaultNewIndexGetter(_ref) {
  var id = _ref.id,
      items = _ref.items,
      activeIndex = _ref.activeIndex,
      overIndex = _ref.overIndex;
  return arrayMove(items, activeIndex, overIndex).indexOf(id);
};

var defaultAnimateLayoutChanges = function defaultAnimateLayoutChanges(_ref2) {
  var containerId = _ref2.containerId,
      isSorting = _ref2.isSorting,
      wasDragging = _ref2.wasDragging,
      index = _ref2.index,
      items = _ref2.items,
      newIndex = _ref2.newIndex,
      previousItems = _ref2.previousItems,
      previousContainerId = _ref2.previousContainerId,
      transition = _ref2.transition;

  if (!transition || !wasDragging) {
    return false;
  }

  if (previousItems !== items && index === newIndex) {
    return false;
  }

  if (isSorting) {
    return true;
  }

  return newIndex !== index && containerId === previousContainerId;
};

var defaultTransition = {
  duration: 200,
  easing: 'ease'
};
var transitionProperty = 'transform';
var disabledTransition = /*#__PURE__*/CSS.Transition.toString({
  property: transitionProperty,
  duration: 0,
  easing: 'linear'
});
var defaultAttributes = {
  roleDescription: 'sortable'
};
/*
 * When the index of an item changes while sorting,
 * we need to temporarily disable the transforms
 */

function useDerivedTransform(_ref) {
  var disabled = _ref.disabled,
      index = _ref.index,
      node = _ref.node,
      rect = _ref.rect;

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      derivedTransform = _useState2[0],
      setDerivedtransform = _useState2[1];

  var previousIndex = useRef(index);
  useIsomorphicLayoutEffect(function () {
    if (!disabled && index !== previousIndex.current && node.current) {
      var initial = rect.current;

      if (initial) {
        var current = getClientRect(node.current, {
          ignoreTransform: true
        });
        var delta = {
          x: initial.left - current.left,
          y: initial.top - current.top,
          scaleX: initial.width / current.width,
          scaleY: initial.height / current.height
        };

        if (delta.x || delta.y) {
          setDerivedtransform(delta);
        }
      }
    }

    if (index !== previousIndex.current) {
      previousIndex.current = index;
    }
  }, [disabled, index, node, rect]);
  useEffect(function () {
    if (derivedTransform) {
      requestAnimationFrame(function () {
        setDerivedtransform(null);
      });
    }
  }, [derivedTransform]);
  return derivedTransform;
}

function useSortable(_ref) {
  var _ref$animateLayoutCha = _ref.animateLayoutChanges,
      animateLayoutChanges = _ref$animateLayoutCha === void 0 ? defaultAnimateLayoutChanges : _ref$animateLayoutCha,
      userDefinedAttributes = _ref.attributes,
      localDisabled = _ref.disabled,
      customData = _ref.data,
      _ref$getNewIndex = _ref.getNewIndex,
      getNewIndex = _ref$getNewIndex === void 0 ? defaultNewIndexGetter : _ref$getNewIndex,
      id = _ref.id,
      localStrategy = _ref.strategy,
      resizeObserverConfig = _ref.resizeObserverConfig,
      _ref$transition = _ref.transition,
      transition = _ref$transition === void 0 ? defaultTransition : _ref$transition;

  var _useContext = useContext(Context),
      items = _useContext.items,
      containerId = _useContext.containerId,
      activeIndex = _useContext.activeIndex,
      globalDisabled = _useContext.disabled,
      disableTransforms = _useContext.disableTransforms,
      sortedRects = _useContext.sortedRects,
      overIndex = _useContext.overIndex,
      useDragOverlay = _useContext.useDragOverlay,
      globalStrategy = _useContext.strategy;

  var disabled = normalizeLocalDisabled(localDisabled, globalDisabled);
  var index = items.indexOf(id);
  var data = useMemo(function () {
    return _objectSpread({
      sortable: {
        containerId: containerId,
        index: index,
        items: items
      }
    }, customData);
  }, [containerId, customData, index, items]);
  var itemsAfterCurrentSortable = useMemo(function () {
    return items.slice(items.indexOf(id));
  }, [items, id]);

  var _useDroppable = useDroppable({
    id: id,
    data: data,
    disabled: disabled.droppable,
    resizeObserverConfig: _objectSpread({
      updateMeasurementsFor: itemsAfterCurrentSortable
    }, resizeObserverConfig)
  }),
      rect = _useDroppable.rect,
      node = _useDroppable.node,
      isOver = _useDroppable.isOver,
      setDroppableNodeRef = _useDroppable.setNodeRef;

  var _useDraggable = useDraggable({
    id: id,
    data: data,
    attributes: _objectSpread(_objectSpread({}, defaultAttributes), userDefinedAttributes),
    disabled: disabled.draggable
  }),
      active = _useDraggable.active,
      activatorEvent = _useDraggable.activatorEvent,
      activeNodeRect = _useDraggable.activeNodeRect,
      attributes = _useDraggable.attributes,
      setDraggableNodeRef = _useDraggable.setNodeRef,
      listeners = _useDraggable.listeners,
      isDragging = _useDraggable.isDragging,
      over = _useDraggable.over,
      setActivatorNodeRef = _useDraggable.setActivatorNodeRef,
      transform = _useDraggable.transform;

  var setNodeRef = useCombinedRefs(setDroppableNodeRef, setDraggableNodeRef);
  var isSorting = Boolean(active);
  var displaceItem = isSorting && !disableTransforms && isValidIndex(activeIndex) && isValidIndex(overIndex);
  var shouldDisplaceDragSource = !useDragOverlay && isDragging;
  var dragSourceDisplacement = shouldDisplaceDragSource && displaceItem ? transform : null;
  var strategy = localStrategy != null ? localStrategy : globalStrategy;
  var finalTransform = displaceItem ? dragSourceDisplacement != null ? dragSourceDisplacement : strategy({
    rects: sortedRects,
    activeNodeRect: activeNodeRect,
    activeIndex: activeIndex,
    overIndex: overIndex,
    index: index
  }) : null;
  var newIndex = isValidIndex(activeIndex) && isValidIndex(overIndex) ? getNewIndex({
    id: id,
    items: items,
    activeIndex: activeIndex,
    overIndex: overIndex
  }) : index;
  var activeId = active == null ? void 0 : active.id;
  var previous = useRef({
    activeId: activeId,
    items: items,
    newIndex: newIndex,
    containerId: containerId
  });
  var itemsHaveChanged = items !== previous.current.items;
  var shouldAnimateLayoutChanges = animateLayoutChanges({
    active: active,
    containerId: containerId,
    isDragging: isDragging,
    isSorting: isSorting,
    id: id,
    index: index,
    items: items,
    newIndex: previous.current.newIndex,
    previousItems: previous.current.items,
    previousContainerId: previous.current.containerId,
    transition: transition,
    wasDragging: previous.current.activeId != null
  });
  var derivedTransform = useDerivedTransform({
    disabled: !shouldAnimateLayoutChanges,
    index: index,
    node: node,
    rect: rect
  });
  useEffect(function () {
    if (isSorting && previous.current.newIndex !== newIndex) {
      previous.current.newIndex = newIndex;
    }

    if (containerId !== previous.current.containerId) {
      previous.current.containerId = containerId;
    }

    if (items !== previous.current.items) {
      previous.current.items = items;
    }
  }, [isSorting, newIndex, containerId, items]);
  useEffect(function () {
    if (activeId === previous.current.activeId) {
      return;
    }

    if (activeId && !previous.current.activeId) {
      previous.current.activeId = activeId;
      return;
    }

    var timeoutId = setTimeout(function () {
      previous.current.activeId = activeId;
    }, 50);
    return function () {
      return clearTimeout(timeoutId);
    };
  }, [activeId]);
  return {
    active: active,
    activeIndex: activeIndex,
    attributes: attributes,
    data: data,
    rect: rect,
    index: index,
    newIndex: newIndex,
    items: items,
    isOver: isOver,
    isSorting: isSorting,
    isDragging: isDragging,
    listeners: listeners,
    node: node,
    overIndex: overIndex,
    over: over,
    setNodeRef: setNodeRef,
    setActivatorNodeRef: setActivatorNodeRef,
    setDroppableNodeRef: setDroppableNodeRef,
    setDraggableNodeRef: setDraggableNodeRef,
    transform: derivedTransform != null ? derivedTransform : finalTransform,
    transition: getTransition()
  };

  function getTransition() {
    if ( // Temporarily disable transitions for a single frame to set up derived transforms
    derivedTransform || // Or to prevent items jumping to back to their "new" position when items change
    itemsHaveChanged && previous.current.newIndex === index) {
      return disabledTransition;
    }

    if (shouldDisplaceDragSource && !isKeyboardEvent(activatorEvent) || !transition) {
      return undefined;
    }

    if (isSorting || shouldAnimateLayoutChanges) {
      return CSS.Transition.toString(_objectSpread(_objectSpread({}, transition), {}, {
        property: transitionProperty
      }));
    }

    return undefined;
  }
}

function normalizeLocalDisabled(localDisabled, globalDisabled) {
  var _localDisabled$dragga, _localDisabled$droppa;

  if (typeof localDisabled === 'boolean') {
    return {
      draggable: localDisabled,
      // Backwards compatibility
      droppable: false
    };
  }

  return {
    draggable: (_localDisabled$dragga = localDisabled == null ? void 0 : localDisabled.draggable) != null ? _localDisabled$dragga : globalDisabled.draggable,
    droppable: (_localDisabled$droppa = localDisabled == null ? void 0 : localDisabled.droppable) != null ? _localDisabled$droppa : globalDisabled.droppable
  };
}

function hasSortableData(entry) {
  if (!entry) {
    return false;
  }

  var data = entry.data.current;

  if (data && 'sortable' in data && typeof data.sortable === 'object' && 'containerId' in data.sortable && 'items' in data.sortable && 'index' in data.sortable) {
    return true;
  }

  return false;
}

var directions = [KeyboardCode.Down, KeyboardCode.Right, KeyboardCode.Up, KeyboardCode.Left];

var sortableKeyboardCoordinates = function sortableKeyboardCoordinates(event, _ref) {
  var _ref$context = _ref.context,
      active = _ref$context.active,
      collisionRect = _ref$context.collisionRect,
      droppableRects = _ref$context.droppableRects,
      droppableContainers = _ref$context.droppableContainers,
      over = _ref$context.over,
      scrollableAncestors = _ref$context.scrollableAncestors;

  if (directions.includes(event.code)) {
    event.preventDefault();

    if (!active || !collisionRect) {
      return;
    }

    var filteredContainers = [];
    droppableContainers.getEnabled().forEach(function (entry) {
      if (!entry || entry != null && entry.disabled) {
        return;
      }

      var rect = droppableRects.get(entry.id);

      if (!rect) {
        return;
      }

      switch (event.code) {
        case KeyboardCode.Down:
          if (collisionRect.top < rect.top) {
            filteredContainers.push(entry);
          }

          break;

        case KeyboardCode.Up:
          if (collisionRect.top > rect.top) {
            filteredContainers.push(entry);
          }

          break;

        case KeyboardCode.Left:
          if (collisionRect.left > rect.left) {
            filteredContainers.push(entry);
          }

          break;

        case KeyboardCode.Right:
          if (collisionRect.left < rect.left) {
            filteredContainers.push(entry);
          }

          break;
      }
    });
    var collisions = closestCorners({
      active: active,
      collisionRect: collisionRect,
      droppableRects: droppableRects,
      droppableContainers: filteredContainers,
      pointerCoordinates: null
    });
    var closestId = getFirstCollision(collisions, 'id');

    if (closestId === (over == null ? void 0 : over.id) && collisions.length > 1) {
      closestId = collisions[1].id;
    }

    if (closestId != null) {
      var activeDroppable = droppableContainers.get(active.id);
      var newDroppable = droppableContainers.get(closestId);
      var newRect = newDroppable ? droppableRects.get(newDroppable.id) : null;
      var newNode = newDroppable == null ? void 0 : newDroppable.node.current;

      if (newNode && newRect && activeDroppable && newDroppable) {
        var newScrollAncestors = getScrollableAncestors(newNode);
        var hasDifferentScrollAncestors = newScrollAncestors.some(function (element, index) {
          return scrollableAncestors[index] !== element;
        });
        var hasSameContainer = isSameContainer(activeDroppable, newDroppable);
        var isAfterActive = isAfter(activeDroppable, newDroppable);
        var offset = hasDifferentScrollAncestors || !hasSameContainer ? {
          x: 0,
          y: 0
        } : {
          x: isAfterActive ? collisionRect.width - newRect.width : 0,
          y: isAfterActive ? collisionRect.height - newRect.height : 0
        };
        var rectCoordinates = {
          x: newRect.left,
          y: newRect.top
        };
        var newCoordinates = offset.x && offset.y ? rectCoordinates : subtract(rectCoordinates, offset);
        return newCoordinates;
      }
    }
  }

  return undefined;
};

function isSameContainer(a, b) {
  if (!hasSortableData(a) || !hasSortableData(b)) {
    return false;
  }

  return a.data.current.sortable.containerId === b.data.current.sortable.containerId;
}

function isAfter(a, b) {
  if (!hasSortableData(a) || !hasSortableData(b)) {
    return false;
  }

  if (!isSameContainer(a, b)) {
    return false;
  }

  return a.data.current.sortable.index < b.data.current.sortable.index;
}

export { SortableContext, arrayMove, arraySwap, defaultAnimateLayoutChanges, defaultNewIndexGetter, hasSortableData, horizontalListSortingStrategy, rectSortingStrategy, rectSwappingStrategy, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy };